/**
 * @module main-impressum
 *
 * Entry point for the legal page.
 *
 * The Impressum is a content page, not an experience: it deliberately boots a
 * small subset of the app — the header, the footer, icon hydration and the
 * background ambience — and skips every interactive section. That keeps the page
 * fast and legal-text focused while preserving the site's visual language.
 */

import "./styles/index.css";

import { registerPlugins, ScrollTrigger } from "./core/gsap-setup.js";
import { installGlobalErrorHandlers, safeInit, reportError } from "./core/errors.js";
import { initAmbient } from "./components/ambient.js";
import { initHeader } from "./components/header.js";
import { initProgressRail } from "./components/progressRail.js";
import { initFooter } from "./components/footer.js";
import { initReveals } from "./animations/reveal.js";
import { hydrateIconSlots } from "./svg/icons.js";
import { CONTACT, COMPANY, LEGAL, BRAND, RESPONSIBLE_FOR_CONTENT } from "./data/site.js";
import { qs } from "./core/dom.js";

/** @type {Array<() => void>} */
let teardowns = [];

/**
 * Render the legal content from the data module.
 *
 * The markup is authored in HTML for crawlers; this fills the dynamic parts
 * (contact rows, company facts) from the single source of truth so the Impressum
 * can never contradict the footer.
 *
 * @returns {void}
 */
function renderLegal() {
  const contactList = qs("[data-legal-contact]");
  if (contactList) {
    contactList.innerHTML = CONTACT.map(
      (entry) => `
      <div>
        <dt>${entry.label}</dt>
        <dd>${entry.href ? `<a href="${entry.href}">${entry.value}</a>` : entry.value}</dd>
      </div>`
    ).join("");
  }

  const factsList = qs("[data-legal-facts]");
  if (factsList) {
    /*
     * Optional disclosures are only rendered when they genuinely apply — a
     * sole trader has no commercial register entry, no managing directors and
     * no supervisory authority, and inventing them would be a false statement
     * about a real person.
     */
    const facts = [
      ["Service provider", COMPANY.owner],
      COMPANY.legalForm ? ["Legal form", COMPANY.legalForm] : null,
      ["Trading name", COMPANY.name],
      ["Address", `${COMPANY.street}, ${COMPANY.postalCode} ${COMPANY.city}, ${COMPANY.country}`],
      COMPANY.register && COMPANY.registerNumber
        ? ["Commercial register", `${COMPANY.register}, ${COMPANY.registerNumber}`]
        : null,
      COMPANY.vatId ? ["VAT identification number", COMPANY.vatId] : null,
      ["Responsible for content (\u00a7 18(2) MStV)", RESPONSIBLE_FOR_CONTENT],
      COMPANY.supervisoryAuthority ? ["Supervisory authority", COMPANY.supervisoryAuthority] : null,
    ].filter(Boolean);

    factsList.innerHTML = facts
      .map(
        ([term, value]) => `
        <div>
          <dt>${term}</dt>
          <dd>${value}</dd>
        </div>`
      )
      .join("");
  }

  const privacyList = qs("[data-legal-privacy]");
  if (privacyList) {
    privacyList.innerHTML = LEGAL.privacy.map((paragraph) => `<li>${paragraph}</li>`).join("");
  }

  const disclaimer = qs("[data-legal-disclaimer]");
  if (disclaimer) disclaimer.textContent = LEGAL.disclaimer;

  const copyright = qs("[data-legal-copyright]");
  if (copyright) copyright.textContent = LEGAL.copyright;

  const trademark = qs("[data-legal-trademark]");
  if (trademark) trademark.textContent = LEGAL.trademark;

  const brandLine = qs("[data-legal-brand]");
  if (brandLine) brandLine.textContent = `${BRAND.name} — ${BRAND.tagline}`;
}

/**
 * Boot the legal page.
 *
 * @returns {() => void} Teardown.
 */
export function boot() {
  teardowns.push(installGlobalErrorHandlers());
  registerPlugins();

  safeInit("icon-slots", () => {
    hydrateIconSlots();
  });

  safeInit("legal-content", () => {
    renderLegal();
  });

  safeInit("ambient", () => initAmbient());
  safeInit("header", () => initHeader({ scroll: null }));
  safeInit("progress-rail", () => initProgressRail());
  safeInit("footer", () => initFooter());
  safeInit("reveals", () => initReveals());

  window.addEventListener("load", () => ScrollTrigger.refresh(), { once: true });

  return () => {
    teardowns.forEach((dispose) => {
      try {
        dispose();
      } catch (error) {
        console.warn("[main-impressum] teardown error", error);
      }
    });
    teardowns = [];
  };
}

try {
  window.__appTeardown = boot();
  window.__reveal?.();
  window.__appReady = true;
} catch (error) {
  window.__reveal?.();
  reportError(error, {
    scope: "main-impressum:boot",
    userMessage: "Part of the page failed to load. The legal information is still available below.",
  });
}
